<template>
  <AdminLayout>
    <div v-if="loaded">
      <div class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900">Customer Invite Details</h3>
          <p class="mt-1 max-w-2xl text-sm text-gray-500">Information about the customer invite.</p>
        </div>
        <div class="border-t border-gray-200">
          <dl>
            <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Team Name</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ customerInvite.team_name }}
              </dd>
            </div>
            <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Contact Email</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ customerInvite.contact_email }}
              </dd>
            </div>
            <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Stripe Plan ID</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ customerInvite.stripe_plan_id }}
              </dd>
            </div>
            <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Stripe Coupon ID</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ customerInvite.stripe_coupon_id || 'None' }}
              </dd>
            </div>
            <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Initial Ad Spend</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ formatCurrency(customerInvite.initial_ad_spend / 100) }}
              </dd>
            </div>
            <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Team ID</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ customerInvite.team_id || 'None' }}
              </dd>
            </div>
            <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Invite URL</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <a :href="inviteUrl" target="_blank" class="text-indigo-600 hover:text-indigo-900">
                  {{ inviteUrl }}
                </a>
              </dd>
            </div>
            <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Created At</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ formatDateTime(customerInvite.created_at) }}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div class="mt-5 flex justify-between">
        <router-link
          :to="{ path: '/admin/customer_invites' }"
          class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:text-gray-800 active:bg-gray-50 transition ease-in-out duration-150"
        >
          Back to List
        </router-link>
      </div>
    </div>

    <LoadingList />
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import AdminLayout from '@/components/layouts/AdminLayout/index.vue';
import LoadingList from '../shared/LoadingList.vue';
import { formatStandardDateTime } from '../../utilities/format.ts';
import type { CustomerInvite } from '../../types/customerInvite';

const loadingSpinnerStore = useLoadingSpinnerStore();
const route = useRoute();

const customerInvite = ref<CustomerInvite>({} as CustomerInvite);
const loaded = ref<boolean>(false);

const inviteUrl = computed<string>(() => {
  return `https://app.humaniz.io/invitations/${customerInvite.value.invite_token}`;
});

const getCustomerInvite = async (): Promise<void> => {
  loadingSpinnerStore.showLoadingSpinner();
  try {
    const response = await fetch(`/api/admin/customer_invites/${route.params.id}`);
    const data = await response.json();

    if (data.record) {
      customerInvite.value = data.record;
    }

    loaded.value = true;
    loadingSpinnerStore.hideLoadingSpinner();
  } catch (error) {
    console.error('Error fetching customer invite:', error);
    loadingSpinnerStore.hideLoadingSpinner();
  }
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatDateTime = (dateTime: string | undefined): string => {
  return dateTime ? formatStandardDateTime(dateTime) : 'N/A';
};

onMounted(async () => {
  await getCustomerInvite();
});
</script>
